import {
  intersectionObserverEmits,
  useIntersectionObserver
} from "@/components/organisms/mixins/intersection-observer.mixin";

export const productListProductsProps = () => ({
  productsList: {
    type: Array,
    default: () => []
  },
  label: {
    type: String,
    required: false
  },
  productsListMode: {
    type: String,
    default: "Grid"
  },
  pagination: {
    type: Object,
    default: () => {}
  },
  pushZones: {
    type: Array,
    default: () => []
  },
  lastPageIndex: {
    type: Number,
    default: 1
  },
  firstPageIndex: {
    type: Number,
    default: 1
  },
  recettePositions: {
    type: Array,
    default: () => []
  },
  pushPositionsObject: {
    type: Object,
    default: null
  },
  doubleVignetteDPPositions: {
    type: Array,
    default: () => []
  },
  pagePushZonesGridCodes: {
    type: Object,
    default: null
  },
  firstImageLazy: {
    type: Boolean,
    required: false,
    default: true
  },
  showAboLad: {
    type: Boolean,
    required: false,
    default: false
  },
  isPro: {
    type: Boolean,
    required: false,
    default: false
  },
  hasClickAndCollectTraiteur: {
    type: Boolean,
    required: false,
    default: false
  },
  hasClickAndCollectTraiteurNoel: {
    type: Boolean,
    required: false,
    default: false
  },
  produitsDejaAchetes: {
    type: Array,
    required: false,
    default: () => []
  },
  sections: {
    type: Array,
    required: false,
    default: () => []
  },
  isLoadingInfinite: {
    type: Boolean,
    required: false,
    default: false
  },
  isRecherche: {
    type: Boolean,
    required: false,
    default: false
  }
});

export const productListProductsEmits = () => [
  "showMoreButtonClicked",
  "showPreviousButtonClicked",
  "fetchFiltredProducts",
  "loadNextPage",
  ...intersectionObserverEmits()
];

export const useProductListProducts = (
  props,
  context,
  filteredPushZones,
  paginator
) => {
  const { onIntersect } = useIntersectionObserver(context);

  const showPlanningClickCollectTraiteur = ref(false);
  const showPlanningClickCollectTraiteurNoel = ref(false);

  const lastApiCall = ref(Date.now());

  const pushPositionsObjectComputed = computed(() => {
    return props.pushPositionsObject[props.productsListMode];
  });

  // on réorganise la premiere page pour y insérer les produits déja achetés en première position, juste après les produits first_in_flow.
  const organizedFirstPageProducts = computed(() => {
    const firstPage = props.sections.find((s) => s.page === 1);
    if (!firstPage) {
      return [];
    }

    if (props.produitsDejaAchetes.length === 0) {
      return firstPage.products;
    }

    const firstPageWithoutDoublonsDejaAchetes = firstPage.products.filter(
      (p) => !props.produitsDejaAchetes.find((pda) => pda.id === p.id)
    );
    const tempList = [...firstPageWithoutDoublonsDejaAchetes];

    const indexToPushProduitsDejaAchetesInFirstPage =
      firstPageWithoutDoublonsDejaAchetes.findIndex(
        (p) => !p.attributes.product_list_item_first_in_flow
      );
    const sortedProduitsDejaAchetes = [...props.produitsDejaAchetes].sort(
      (a, b) =>
        b.attributes.product_list_item_first_in_flow -
        a.attributes.product_list_item_first_in_flow
    );

    tempList.splice(
      indexToPushProduitsDejaAchetesInFirstPage,
      0,
      ...sortedProduitsDejaAchetes
    );

    return tempList;
  });

  const clickPage = (index) => {
    context.emit("showMoreButtonClicked", false);
    context.emit("showPreviousButtonClicked", false);
    context.emit("loadNextPage", {
      page: index
    });
  };
  const showMore = () => {
    context.emit("showMoreButtonClicked", true);
    context.emit("showPreviousButtonClicked", false);
    context.emit("loadNextPage", {
      page: props.lastPageIndex + 1
    });
  };
  const showPrevious = () => {
    context.emit("showPreviousButtonClicked", true);
    context.emit("showMoreButtonClicked", false);
    context.emit("loadNextPage", {
      page: props.firstPageIndex - 1
    });
  };
  const formattedPrice = (price) => {
    const fixedAmount = price ? parseFloat(price).toFixed(2) : price;
    return fixedAmount;
  };
  const hasPushZone = (index) => {
    if (!props.pushPositionsObject) {
      return null;
    }
    const indexList = pushPositionsObjectComputed.value;
    if (indexList === undefined) {
      return null;
    }
    const pushIndex = indexList.indexOf(index);
    if (pushIndex === -1) {
      return false;
    }
    return pushIndex < filteredPushZones.value.length;
  };
  const getPushZone = (index) => {
    if (!props.pushPositionsObject) {
      return null;
    }
    const indexList = pushPositionsObjectComputed.value;
    if (indexList === undefined) {
      return null;
    }
    const pushZonesIndex = indexList.indexOf(index);
    return pushZonesIndex > -1 &&
      pushZonesIndex < filteredPushZones.value.length
      ? filteredPushZones.value[pushZonesIndex]
      : null;
  };
  const showInDoubleVignette = (index, product) => {
    return (
      product.attributes.is_double_vignette &&
      props.doubleVignetteDPPositions.includes(index)
    );
  };

  const showRecette = (index, sectionIndex) => {
    return (
      !hasPushZone(index + 1) &&
      props.recettePositions.includes(index + 1) &&
      sectionIndex === 0
    );
  };

  const handleScroll = () => {
    if (
      window.scrollY + window.innerHeight + 1000 >
        paginator.value?.$el.offsetTop &&
      window.scrollY < paginator.value?.$el.offsetTop &&
      lastApiCall.value < Date.now() + 1000 &&
      !props.isLoadingInfinite
    ) {
      if (
        props.pagination.current_page !== props.pagination.total_pages &&
        props.lastPageIndex !== props.pagination.total_pages &&
        props.pagination.nb_items < props.pagination.nb_total_items
      ) {
        lastApiCall.value = Date.now();
        showMore();
      }
    }
  };

  const mounted = () => {
    if (process.client) {
      window.addEventListener("scroll", handleScroll);
    }
  };

  const unmount = () => {
    if (process.client) {
      window.removeEventListener("scroll", handleScroll);
    }
  };

  return {
    onIntersect,
    showPlanningClickCollectTraiteur,
    showPlanningClickCollectTraiteurNoel,
    pushPositionsObjectComputed,
    organizedFirstPageProducts,
    clickPage,
    showMore,
    showPrevious,
    formattedPrice,
    hasPushZone,
    getPushZone,
    showInDoubleVignette,
    showRecette,
    mounted,
    unmount
  };
};
